import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { AccessTokenResponse, AuthState, CurrentClient, LoginFailure, LoginRequest, SessionRefreshFailure } from './_authTypes';
import { handleError } from '../../utils/handleError';
import { handleAxiosError, isServiceUnavailableError } from '../../axios/handleAxiosError';
import { buildApiUrl } from '../../axios/urlCreator';
import { broadcastLogout, clearAccessToken, setAccessToken } from '../../axios/authSession';
import { clearResponseCache } from '../../axios/responseCache';

const initialState: AuthState = {
    currentClient: null,
    initialized: false,
    loading: false,
    error: null,
    sessionEndReason: null,
};

const legacyTokenKeys = ['CAT', 'CATE', 'CRT', 'ITFA', 'IPCR', 'TENANT', 'ITFAVC'];

const clearBrowserSession = (notifyOtherTabs = true) => {
    clearAccessToken();
    clearResponseCache();
    if (notifyOtherTabs) broadcastLogout();
    legacyTokenKeys.forEach((key) => localStorage.removeItem(key));
    sessionStorage.removeItem('oodd');
    sessionStorage.removeItem('pppfff');

    if (localStorage.getItem('rememberMe')?.toLowerCase() !== 'true') {
        localStorage.removeItem('USERNAME');
        localStorage.removeItem('rememberMe');
    }
};

const parseJwt = (token: string): Record<string, string> => {
    const payload = token.split('.')[1];
    if (!payload) throw new Error('The access token is malformed.');

    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '=');
    const bytes = Uint8Array.from(atob(normalized), (character) => character.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
};

const toBoolean = (value?: string) => value?.toLowerCase() === 'true';

const createClient = (response: AccessTokenResponse, fallbackUserName = ''): CurrentClient => {
    if (response.isTwoFactorActivated && !response.accessToken) {
        if (!response.twoFactorChallengeToken) throw new Error('The two-factor login challenge is missing.');
        clearAccessToken();
        return {
            userName: fallbackUserName,
            tenant: '',
            itfa: true,
            ipcr: false,
            itfavc: false,
            twoFactorChallengeToken: response.twoFactorChallengeToken,
        };
    }

    if (!response.accessToken) throw new Error('The access token is missing.');
    const claims = parseJwt(response.accessToken);
    setAccessToken(response.accessToken);

    return {
        userName: claims.userName ?? fallbackUserName,
        tenant: claims.tenant ?? '',
        itfa: toBoolean(claims.itfa),
        ipcr: toBoolean(claims.ipcr),
        itfavc: toBoolean(claims.itfa),
        twoFactorChallengeToken: null,
    };
};

export const loginAndFetchClient = createAsyncThunk<AccessTokenResponse, LoginRequest, { rejectValue: LoginFailure }>(
    'auth/loginAndFetchClient',
    async (loginRequest, { rejectWithValue }) => {
        try {
            const response = await axios.post<AccessTokenResponse>(buildApiUrl('token/login'), loginRequest);
            return response.data;
        } catch (error) {
            const responseData = axios.isAxiosError(error) ? error.response?.data : null;
            const captchaRequired = Boolean(
                responseData &&
                typeof responseData === 'object' &&
                'captchaRequired' in responseData &&
                responseData.captchaRequired === true
            );

            return rejectWithValue({
                message: axios.isAxiosError(error) ? handleAxiosError(error) : handleError(error),
                captchaRequired,
            });
        }
    },
);

export const refreshAccessToken = createAsyncThunk<AccessTokenResponse, void, { rejectValue: SessionRefreshFailure }>(
    'auth/refreshAccessToken',
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.post<AccessTokenResponse>(buildApiUrl('token/refreshTokenLogin'));
            return response.data;
        } catch (error) {
            return rejectWithValue({
                message: axios.isAxiosError(error) ? handleAxiosError(error) : handleError(error),
                serviceUnavailable: isServiceUnavailableError(error),
            });
        }
    },
);

export const logoutSession = createAsyncThunk(
    'auth/logoutSession',
    async () => {
        try {
            await axios.post(buildApiUrl('token/logout'));
        } catch {
            // Local logout still completes when the session has already expired or the network is unavailable.
        }
    },
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout(state) {
            state.currentClient = null;
            state.initialized = true;
            state.loading = false;
            state.error = null;
            state.sessionEndReason = null;
            clearBrowserSession();
        },
        logoutForServiceUnavailable(state) {
            state.currentClient = null;
            state.initialized = true;
            state.loading = false;
            state.error = null;
            state.sessionEndReason = 'service-unavailable';
            clearBrowserSession();
        },
        logoutFromAnotherTab(state) {
            state.currentClient = null;
            state.initialized = true;
            state.loading = false;
            state.error = null;
            state.sessionEndReason = null;
            clearBrowserSession(false);
        },
        setCurrentClient(state, action: PayloadAction<AccessTokenResponse | null>) {
            if (action.payload) {
                state.currentClient = createClient(action.payload);
                state.initialized = true;
                state.error = null;
                state.sessionEndReason = null;
            } else {
                state.currentClient = null;
                state.initialized = true;
                state.sessionEndReason = null;
                clearBrowserSession();
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginAndFetchClient.pending, (state) => {
                state.loading = true;
                state.error = null;
                state.sessionEndReason = null;
            })
            .addCase(loginAndFetchClient.fulfilled, (state, action) => {
                clearResponseCache();
                state.loading = false;
                state.initialized = true;
                state.currentClient = createClient(action.payload, action.meta.arg.userName);
                state.sessionEndReason = null;
            })
            .addCase(loginAndFetchClient.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = action.payload?.message ?? action.error.message ?? null;
                state.currentClient = null;
                state.sessionEndReason = null;
            })
            .addCase(refreshAccessToken.pending, (state) => {
                state.loading = !state.initialized;
            })
            .addCase(refreshAccessToken.fulfilled, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = null;
                state.currentClient = createClient(action.payload);
                state.sessionEndReason = null;
            })
            .addCase(refreshAccessToken.rejected, (state, action) => {
                state.loading = false;
                state.initialized = true;
                state.error = null;
                state.currentClient = null;
                state.sessionEndReason = action.payload?.serviceUnavailable ? 'service-unavailable' : null;
                clearBrowserSession();
            })
            .addCase(logoutSession.pending, (state) => {
                state.currentClient = null;
                state.initialized = true;
                state.error = null;
                state.sessionEndReason = null;
                clearBrowserSession();
            });
    },
});

export const { logout, logoutForServiceUnavailable, logoutFromAnotherTab, setCurrentClient } = authSlice.actions;
export default authSlice.reducer;
